/**
 * CurrentExaminationPanel — CPT-driven examination context panel.
 *
 * Section 2 of the top layout on TranscriptionPage.
 *
 * UX flow:
 * 1. CPT exam search / auto-detect at the top (drives exam context)
 * 2. Study fields auto-populate from selected CPT exam
 * 3. User can override any auto-filled field
 * 4. Remaining fields (accession, study date, clinical indication, etc.) filled manually
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import type { PatientContextInput } from '../services/patientService';
import type { CPTExam } from '../services/cptExamService';
import { resolveLaterality } from '../services/cptExamService';
import CPTExamSelector from './CPTExamSelector';
import TemplatePreviewCard from './rxf/TemplatePreviewCard';
import PriorReportPanel from './rxf/PriorReportPanel';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import { validatePatientExam, clearValidation } from '../store/validationSlice';
import { MODALITIES, BODY_REGIONS, LATERALITY_OPTIONS, STUDY_PRIORITIES } from '../constants/radiology';
import { typography } from '~/theme';

interface CurrentExaminationPanelProps {
  value: PatientContextInput;
  onChange: (ctx: PatientContextInput) => void;
  disabled?: boolean;
  /** When bumped by parent (e.g. after main-page Save), resets the dirty state */
  savedAt?: number;
  /** Called for system-initiated auto-population so parent can sync lastSavedPatientContext */
  onSystemChange?: (ctx: PatientContextInput) => void;
  /** Called when the user explicitly clears the CPT exam selection */
  onCPTCleared?: () => void;
  /** Pass-through props for CPTExamSelector */
  isTranscribing?: boolean;
  isReadOnly?: boolean;
  correctedText?: string;
  rawText?: string;
  isFinal?: boolean;
  report?: {
    cpt_detection_method?: string | null;
    cpt_detection_confidence?: string | null;
  } | null;
  /** RXF Phase 3: session ID for prior report auto-fetch */
  sessionId?: string;
}

/** Fields that are auto-populated from a CPT exam selection */
type AutoFillableField = 'modality' | 'body_part' | 'study_description' | 'laterality';

const CurrentExaminationPanel: React.FC<CurrentExaminationPanelProps> = ({
  value,
  onChange,
  onSystemChange,
  onCPTCleared,
  disabled = false,
  savedAt,
  isTranscribing = false,
  isReadOnly = false,
  correctedText = '',
  rawText = '',
  isFinal = false,
  report,
  sessionId,
}) => {
  const dispatch = useAppDispatch();
  const { selectedExam, detection } = useAppSelector((state) => state.cptExams);

  // Track which fields were auto-filled (so we can show indicator chips)
  const autoFilledRef = useRef<Set<AutoFillableField>>(new Set());

  // Only track the exam/study fields for dirty state — patient demographics (name, MRN, DOB, gender)
  // are managed by PatientDemographicsPanel and should not affect this panel's unsaved indicator.
  const examFields = (v: PatientContextInput) => ({
    modality: v.modality,
    body_part: v.body_part,
    study_description: v.study_description,
    laterality: v.laterality,
    study_priority: v.study_priority,
    accession_number: v.accession_number,
    study_date: v.study_date,
    clinical_indication: v.clinical_indication,
    ordering_physician_name: v.ordering_physician_name,
  });

  const [savedExamValue, setSavedExamValue] = useState(() => examFields(value));
  const isDirty = JSON.stringify(examFields(value)) !== JSON.stringify(savedExamValue);

  // When the parent signals a save completed (e.g. main-page Save button), clear dirty state
  useEffect(() => {
    if (savedAt !== undefined) {
      setSavedExamValue(examFields(value));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAt]);

  // Combined system-initiated field initialisation:
  //   - Auto-populate CPT study fields when selectedExam arrives (session restore or manual select)
  //   - Default study_date to today if empty
  // Both updates are committed in a single onChange to avoid stale-closure races that would
  // otherwise let one effect clobber the other on first render.
  const prevSelectedExamRef = useRef<string | null>(null);
  useEffect(() => {
    const examKey = selectedExam
      ? `${selectedExam.cpt_code}::${resolveLaterality(selectedExam) || ''}`
      : null;
    const cptChanged = !!selectedExam && examKey !== prevSelectedExamRef.current;
    const needStudyDate = !value.study_date;
    if (!cptChanged && !needStudyDate) return;

    const updates: Partial<PatientContextInput> = {};
    const filled = new Set<AutoFillableField>();

    if (cptChanged && selectedExam) {
      prevSelectedExamRef.current = examKey;
      if (selectedExam.modality && !value.modality) { updates.modality = selectedExam.modality; filled.add('modality'); }
      if (selectedExam.body_region && !value.body_part) { updates.body_part = selectedExam.body_region; filled.add('body_part'); }
      if (selectedExam.exam_name && !value.study_description) { updates.study_description = selectedExam.exam_name; filled.add('study_description'); }
      const resolvedLaterality =
        resolveLaterality(selectedExam)
        || (detection?.laterality && detection.laterality !== 'none' ? detection.laterality : null);
      if (resolvedLaterality && !value.laterality) {
        updates.laterality = resolvedLaterality;
        filled.add('laterality');
      }
    }

    if (needStudyDate) {
      updates.study_date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    }

    if (Object.keys(updates).length > 0) {
      if (filled.size > 0) autoFilledRef.current = new Set([...autoFilledRef.current, ...filled]);
      const merged = { ...value, ...updates };
      // Notify parent via system-change callback so it can update both
      // patientContext AND lastSavedPatientContext together, avoiding dirty state.
      if (onSystemChange) {
        onSystemChange(merged);
      } else {
        onChange(merged);
      }
      // System-initiated changes are the new saved baseline (not user-driven dirty edits)
      setSavedExamValue(examFields(merged));
    } else if (cptChanged) {
      // CPT exam changed but all fields were already populated (e.g. session load from history).
      // Still rebaseline so the panel doesn't appear dirty against the pre-load empty baseline.
      setSavedExamValue(examFields(value));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam?.cpt_code, value.study_date]);

  // Separate effect: apply detection.laterality when it arrives AFTER selectedExam is
  // already set (common path: CPT exam fetch completes before LLM detection finishes).
  useEffect(() => {
    const examLat = selectedExam ? resolveLaterality(selectedExam) : null;
    if (
      selectedExam &&
      detection?.laterality &&
      detection.laterality !== 'none' &&
      !value.laterality &&
      !examLat
    ) {
      autoFilledRef.current = new Set([...autoFilledRef.current, 'laterality']);
      const merged = { ...value, laterality: detection.laterality };
      onChange(merged);
      setSavedExamValue(examFields(merged));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detection?.laterality, selectedExam?.cpt_code]);

  // Re-validate when patient demographics or the selected exam change.
  // Dictation-driven validation is handled by the separate debounced effect.
  useEffect(() => {
    if (!selectedExam) {
      dispatch(clearValidation());
      return;
    }
    dispatch(validatePatientExam({
      patient_gender: value.gender || null,
      patient_dob: value.dob || null,
      cpt_code: selectedExam.cpt_code,
      exam_name: selectedExam.exam_name,
      modality: selectedExam.modality,
      body_part: selectedExam.body_region,
      laterality: resolveLaterality(selectedExam),
      contrast: selectedExam.contrast,
      trigger: 'patient_selected',
      dictation_text: correctedText || rawText || null,
      use_llm: true,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.gender, value.dob, selectedExam?.cpt_code, dispatch]);

  const handleField = useCallback(
    (field: keyof PatientContextInput) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // User manually edited → remove auto-fill marker
        autoFilledRef.current.delete(field as AutoFillableField);
        onChange({ ...value, [field]: e.target.value || undefined });
      },
    [value, onChange],
  );

  // Auto-populate study fields from selected CPT exam
  const handleCPTExamSelected = useCallback(
    (exam: CPTExam | null) => {
      if (!exam) {
        // Clear auto-filled fields
        const cleared: Partial<PatientContextInput> = {};
        autoFilledRef.current.forEach((field) => {
          cleared[field] = undefined;
        });
        autoFilledRef.current.clear();
        onChange({ ...value, ...cleared });
        onCPTCleared?.();
        return;
      }

      const updates: Partial<PatientContextInput> = {};
      const filled = new Set<AutoFillableField>();

      // Modality
      if (exam.modality) {
        updates.modality = exam.modality;
        filled.add('modality');
      }
      // Body Part
      if (exam.body_region) {
        updates.body_part = exam.body_region;
        filled.add('body_part');
      }
      // Study Description — compose from exam name
      if (exam.exam_name) {
        updates.study_description = exam.exam_name;
        filled.add('study_description');
      }
      // Laterality — store as-is (dataset uses lowercase: left, right, bilateral)
      const resolvedLat = resolveLaterality(exam);
      if (resolvedLat) {
        updates.laterality = resolvedLat;
        filled.add('laterality');
      }

      autoFilledRef.current = filled;
      onChange({ ...value, ...updates });

      console.log(
        'EXAM_FIELDS_AUTO_POPULATED | cpt_code=%s | fields=%s',
        exam.cpt_code,
        Array.from(filled).join(','),
      );

      // Validation is handled by the useEffect that watches selectedExam
      // and patient demographics, so we do not dispatch it here.
    },
    [value, onChange, onCPTCleared],
  );

  // Re-validate when dictated text changes substantially (debounced).
  // This catches wrong-exam scenarios where the user dictates about one body
  // part but has a different CPT exam selected.
  useEffect(() => {
    if (!selectedExam) return;
    const text = correctedText || rawText || '';
    if (text.length < 20) return;

    const timer = setTimeout(() => {
      dispatch(validatePatientExam({
        patient_gender: value.gender || null,
        patient_dob: value.dob || null,
        cpt_code: selectedExam.cpt_code,
        exam_name: selectedExam.exam_name,
        modality: selectedExam.modality,
        body_part: selectedExam.body_region,
        laterality: resolveLaterality(selectedExam),
        contrast: selectedExam.contrast,
        trigger: 'dictation_text_changed',
        dictation_text: text,
        use_llm: true,
      }));
    }, 800);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctedText, rawText, selectedExam?.cpt_code, value.gender, value.dob, dispatch]);

  // Helper to check if a field was auto-filled
  const isAutoFilled = (field: AutoFillableField) => autoFilledRef.current.has(field);

  // Small indicator for auto-filled fields
  const autoFillAdornment = (field: AutoFillableField) =>
    isAutoFilled(field) ? (
      <Chip
        icon={<AutoFixHighIcon sx={{ fontSize: 12 }} aria-hidden="true" />}
        label="CPT"
        size="small"
        color="success"
        variant="outlined"
        sx={{ height: 18, fontSize: typography.overline.fontSize, ml: 0.5 }}
      />
    ) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={typography.h1.fontWeight} sx={{ fontSize: typography.body2.fontSize }}>
            Current Examination
          </Typography>
        </Box>
        {isDirty && !isReadOnly && (
          <Tooltip title="Unsaved changes — use the Save button at the top" arrow>
            <Chip
              label="Unsaved"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 20, fontSize: typography.caption.fontSize }}
            />
          </Tooltip>
        )}
      </Box>

      {/* ── CPT Exam Search (drives exam context) ── */}
      <CPTExamSelector
        onSelect={handleCPTExamSelected}
        disabled={isTranscribing || isReadOnly}
        detectedModality={null}
        detectedMethod={report?.cpt_detection_method}
        autoDetectTranscript={isFinal && !isTranscribing ? (correctedText || rawText) : null}
      />

      {/* ── RXF Phase 1: Template Preview ── */}
      {selectedExam && (
        <TemplatePreviewCard
          cptCode={selectedExam.cpt_code}
          modality={selectedExam.modality}
          bodyRegion={selectedExam.body_region}
        />
      )}

      {/* ── RXF Phase 3: Prior Report Comparison ── */}
      {sessionId && (
        <PriorReportPanel
          sessionId={sessionId}
          patientId={value.mrn}
        />
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* ── Study-level fields (CPT auto-filled first, then manual) ── */}
      <Grid container spacing={1.5}>
        {/* Row 1: Modality + Body Part (auto-filled from CPT) */}
        <Grid item xs={6}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              select
              label="Modality"
              value={value.modality || ''}
              onChange={handleField('modality')}
              size="small"
              fullWidth
              disabled={disabled}
            >
              <MenuItem value="">—</MenuItem>
              {MODALITIES.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ position: 'absolute', top: -8, right: 4 }}>
              {autoFillAdornment('modality')}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              select
              label="Body Part / Anatomy"
              value={value.body_part || ''}
              onChange={handleField('body_part')}
              size="small"
              fullWidth
              disabled={disabled}
            >
              <MenuItem value="">—</MenuItem>
              {BODY_REGIONS.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ position: 'absolute', top: -8, right: 4 }}>
              {autoFillAdornment('body_part')}
            </Box>
          </Box>
        </Grid>

        {/* Row 2: Study Description + Laterality (auto-filled from CPT) */}
        <Grid item xs={6}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              label="Study Description"
              value={value.study_description || ''}
              onChange={handleField('study_description')}
              size="small"
              fullWidth
              disabled={disabled}
              placeholder="e.g., MRI Brain with contrast"
            />
            <Box sx={{ position: 'absolute', top: -8, right: 4 }}>
              {autoFillAdornment('study_description')}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              select
              label="Laterality"
              value={value.laterality || ''}
              onChange={handleField('laterality')}
              size="small"
              fullWidth
              disabled={disabled}
            >
              <MenuItem value="">—</MenuItem>
              {LATERALITY_OPTIONS.map((l) => (
                <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ position: 'absolute', top: -8, right: 4 }}>
              {autoFillAdornment('laterality')}
            </Box>
          </Box>
        </Grid>

        {/* Row 3: Study Priority + Accession Number (manual) */}
        <Grid item xs={6}>
          <TextField
            select
            label="Study Priority"
            value={value.study_priority || ''}
            onChange={handleField('study_priority')}
            size="small"
            fullWidth
            disabled={disabled}
          >
            <MenuItem value="">—</MenuItem>
            {STUDY_PRIORITIES.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Accession Number"
            value={value.accession_number || ''}
            onChange={handleField('accession_number')}
            size="small"
            fullWidth
            disabled={disabled}
          />
        </Grid>

        {/* Row 4: Study Date + Clinical Indication (manual / PACS-only) */}
        <Grid item xs={6}>
          <TextField
            label="Study Date"
            type="date"
            value={value.study_date || ''}
            onChange={handleField('study_date')}
            size="small"
            fullWidth
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Clinical Indication"
            value={value.clinical_indication || ''}
            onChange={handleField('clinical_indication')}
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="e.g., Headaches, rule out mass"
          />
        </Grid>

        {/* Row 5: Ordering Physician (manual / PACS-only) */}
        <Grid item xs={12}>
          <TextField
            label="Ordering Physician"
            value={value.ordering_physician_name || ''}
            onChange={handleField('ordering_physician_name')}
            size="small"
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CurrentExaminationPanel;
